/*!
 * Nono Carousel - vanilla replacement for jQuery + Slick.
 * MIT-licensed inspiration from Slick API surface; written from scratch for Nono.
 * No dependencies. ~6 KB. Lazy-initializes on first scroll into viewport.
 *
 * Usage:
 *   NonoCarousel.init(rootEl, {
 *     slidesToShow: 4,
 *     slidesToScroll: 1,
 *     arrows: true,
 *     prevArrow: '.my-prev',        // selector or element or HTML string
 *     nextArrow: '.my-next',
 *     dots: true,
 *     dotsContainer: '.my-dots',    // optional - auto-creates if absent
 *     autoplay: false,
 *     autoplaySpeed: 3000,
 *     infinite: false,
 *     fade: false,                  // fade mode (absolute + opacity)
 *     draggable: true,
 *     swipe: true,
 *     asNavFor: '.thumb-carousel',  // syncs another carousel
 *     focusOnSelect: false,
 *     responsive: [
 *       { breakpoint: 1024, settings: { slidesToShow: 2 } },
 *       { breakpoint: 640,  settings: { slidesToShow: 1 } }
 *     ]
 *   });
 *
 * Adds Slick-compatible classes so existing CSS keeps working:
 *   .slick-initialized, .slick-list, .slick-track, .slick-slide,
 *   .slick-active, .slick-current, .slick-dots, .slick-prev/next,
 *   .slick-disabled, .slick-dotted, .slick-slider
 */
(function (global) {
  'use strict';

  var registry = new WeakMap();

  function NonoCarousel(root, opts) {
    if (!(this instanceof NonoCarousel)) return new NonoCarousel(root, opts);
    if (typeof root === 'string') root = document.querySelector(root);
    if (!root || registry.has(root)) return registry.get(root);

    this.root = root;
    this.opts = Object.assign(
      {
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: true,
        prevArrow: null,
        nextArrow: null,
        dots: false,
        dotsContainer: null,
        autoplay: false,
        autoplaySpeed: 3000,
        infinite: false,
        fade: false,
        draggable: true,
        swipe: true,
        asNavFor: null,
        focusOnSelect: false,
        responsive: [],
      },
      opts || {}
    );

    this.current = 0;
    this.handlers = {};
    this.linkedNav = null;
    this._setupDom();
    this._applyResponsive();
    this._buildArrows();
    this._buildDots();
    this._bindEvents();
    this._update(true);
    this._setupNav();
    this._setupAutoplay();

    registry.set(root, this);
    this.emit('init', this);
  }

  NonoCarousel.prototype._setupDom = function () {
    var root = this.root;
    root.classList.add('slick-initialized', 'slick-slider');
    if (this.opts.dots) root.classList.add('slick-dotted');

    // Wrap original children in slick-list > slick-track if not already wrapped.
    var existingTrack = root.querySelector(':scope > .slick-list > .slick-track');
    var slides;
    if (existingTrack) {
      slides = Array.from(existingTrack.children);
    } else {
      slides = Array.from(root.children).filter(function (el) {
        return (
          el.tagName !== 'BUTTON' &&
          !el.classList.contains('slick-prev') &&
          !el.classList.contains('slick-next') &&
          !el.classList.contains('slick-dots')
        );
      });
      var list = document.createElement('div');
      list.className = 'slick-list';
      var track = document.createElement('div');
      track.className = 'slick-track';
      slides.forEach(function (s) {
        s.classList.add('slick-slide');
        track.appendChild(s);
      });
      list.appendChild(track);
      root.insertBefore(list, root.firstChild);
    }
    this.list = root.querySelector(':scope > .slick-list');
    this.track = root.querySelector(':scope > .slick-list > .slick-track');
    this.slides = Array.from(this.track.children);

    if (this.opts.fade) {
      root.classList.add('nono-carousel--fade');
      this.list.style.position = 'relative';
      this.list.style.overflow = 'hidden';
      /* CSS Grid stack: all slides share grid-area 'slide', so the track
         auto-sizes to the natural height of whichever slide is largest,
         and slides overlap perfectly without absolute positioning. This
         avoids the collapsed-container bug that 'position: absolute' + no
         explicit parent height causes. */
      this.track.style.display = 'grid';
      this.track.style.gridTemplateAreas = '"slide"';
      this.track.style.gridTemplateColumns = '1fr';
      this.slides.forEach(function (s, i) {
        s.style.gridArea = 'slide';
        s.style.opacity = i === 0 ? '1' : '0';
        s.style.transition = 'opacity 350ms ease';
        s.style.pointerEvents = i === 0 ? '' : 'none';
      });
    } else {
      root.classList.add('nono-carousel--slide');
      this.list.style.overflow = 'hidden';
      this.track.style.display = 'flex';
      this.track.style.transition = 'transform 320ms ease';
      this.track.style.willChange = 'transform';
    }
  };

  NonoCarousel.prototype._applyResponsive = function () {
    var bps = (this.opts.responsive || []).slice().sort(function (a, b) {
      return b.breakpoint - a.breakpoint;
    });
    var w = window.innerWidth;
    this.activeSettings = Object.assign({}, this.opts);
    for (var i = 0; i < bps.length; i++) {
      if (w <= bps[i].breakpoint) {
        this.activeSettings = Object.assign({}, this.opts, bps[i].settings);
      }
    }
  };

  NonoCarousel.prototype._buildArrows = function () {
    if (!this.activeSettings.arrows) return;
    var resolve = function (val, defaultHtml) {
      if (!val) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.innerHTML = defaultHtml;
        return btn;
      }
      if (typeof val === 'string') {
        if (val.trim().charAt(0) === '<') {
          var wrap = document.createElement('div');
          wrap.innerHTML = val.trim();
          return wrap.firstElementChild;
        }
        return document.querySelector(val);
      }
      if (val instanceof Element) return val;
      // jQuery-like object
      if (val && val[0] instanceof Element) return val[0];
      return null;
    };
    this.prev = resolve(
      this.opts.prevArrow,
      '<span aria-hidden="true">&#8249;</span><span class="visually-hidden">Previous</span>'
    );
    this.next = resolve(
      this.opts.nextArrow,
      '<span aria-hidden="true">&#8250;</span><span class="visually-hidden">Next</span>'
    );
    if (this.prev) {
      this.prev.classList.add('slick-prev');
      this.prev.setAttribute('type', 'button');
      if (!this.prev.parentNode) this.root.appendChild(this.prev);
    }
    if (this.next) {
      this.next.classList.add('slick-next');
      this.next.setAttribute('type', 'button');
      if (!this.next.parentNode) this.root.appendChild(this.next);
    }
  };

  NonoCarousel.prototype._buildDots = function () {
    if (!this.activeSettings.dots) return;
    var container;
    if (this.opts.dotsContainer) {
      container =
        typeof this.opts.dotsContainer === 'string'
          ? document.querySelector(this.opts.dotsContainer)
          : this.opts.dotsContainer;
    }
    if (!container) {
      container = this.root.querySelector(':scope > .slick-dots');
    }
    if (!container) {
      container = document.createElement('ul');
      container.className = 'slick-dots';
      this.root.appendChild(container);
    }
    container.innerHTML = '';
    var pages = this._pageCount();
    var self = this;
    for (var i = 0; i < pages; i++) {
      var li = document.createElement('li');
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.setAttribute('aria-label', 'Go to slide ' + (i + 1));
      btn.textContent = String(i + 1);
      (function (idx) {
        btn.addEventListener('click', function () {
          self.goTo(idx * self.activeSettings.slidesToScroll);
        });
      })(i);
      li.appendChild(btn);
      container.appendChild(li);
    }
    this.dots = container;
  };

  NonoCarousel.prototype._pageCount = function () {
    var per = this.activeSettings.slidesToShow;
    var step = this.activeSettings.slidesToScroll;
    var n = this.slides.length;
    if (this.activeSettings.fade) return n;
    return Math.max(1, Math.ceil((n - per) / step) + 1);
  };

  NonoCarousel.prototype._bindEvents = function () {
    var self = this;
    if (this.prev) this.prev.addEventListener('click', function () { self.prevSlide(); });
    if (this.next) this.next.addEventListener('click', function () { self.nextSlide(); });

    if (this.activeSettings.swipe && !this.activeSettings.fade) {
      var startX = 0, startY = 0, deltaX = 0, dragging = false;
      this.list.addEventListener('touchstart', function (e) {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        deltaX = 0;
        dragging = true;
      }, { passive: true });
      this.list.addEventListener('touchmove', function (e) {
        if (!dragging) return;
        deltaX = e.touches[0].clientX - startX;
        var dy = e.touches[0].clientY - startY;
        if (Math.abs(deltaX) > Math.abs(dy) * 1.4) {
          // horizontal intent
        }
      }, { passive: true });
      this.list.addEventListener('touchend', function () {
        if (!dragging) return;
        dragging = false;
        if (deltaX > 50) self.prevSlide();
        else if (deltaX < -50) self.nextSlide();
      });
    }

    if (this.activeSettings.focusOnSelect) {
      this.slides.forEach(function (slide, idx) {
        slide.style.cursor = 'pointer';
        slide.addEventListener('click', function () { self.goTo(idx); });
      });
    }

    this._onResize = function () {
      var prevShow = self.activeSettings.slidesToShow;
      self._applyResponsive();
      if (prevShow !== self.activeSettings.slidesToShow) {
        self._buildDots();
      }
      self._update();
    };
    window.addEventListener('resize', this._onResize, { passive: true });
  };

  NonoCarousel.prototype._setupAutoplay = function () {
    if (!this.activeSettings.autoplay) return;
    var self = this;
    var play = function () {
      self._timer = setTimeout(function () {
        self.nextSlide(true);
        play();
      }, self.activeSettings.autoplaySpeed);
    };
    play();
    this.root.addEventListener('mouseenter', function () { clearTimeout(self._timer); });
    this.root.addEventListener('mouseleave', play);
  };

  NonoCarousel.prototype._setupNav = function () {
    if (!this.opts.asNavFor) return;
    var target;
    if (typeof this.opts.asNavFor === 'string') {
      var el = document.querySelector(this.opts.asNavFor);
      target = el ? registry.get(el) : null;
    } else if (this.opts.asNavFor instanceof NonoCarousel) {
      target = this.opts.asNavFor;
    }
    this.linkedNav = target || null;
  };

  NonoCarousel.prototype._update = function (skipNavSync) {
    var self = this;
    var s = this.activeSettings;
    var max = s.fade
      ? this.slides.length - 1
      : Math.max(0, this.slides.length - s.slidesToShow);
    this.current = Math.max(0, Math.min(this.current, max));

    if (s.fade) {
      this.slides.forEach(function (slide, idx) {
        var isCur = idx === self.current;
        slide.style.opacity = isCur ? '1' : '0';
        slide.style.pointerEvents = isCur ? '' : 'none';
        slide.classList.toggle('slick-active', isCur);
        slide.classList.toggle('slick-current', isCur);
      });
    } else {
      var slideWidth = this.list.clientWidth / s.slidesToShow;
      this.slides.forEach(function (slide) {
        slide.style.flex = '0 0 ' + slideWidth + 'px';
        slide.style.minWidth = slideWidth + 'px';
        slide.style.maxWidth = slideWidth + 'px';
      });
      var offset = -slideWidth * this.current;
      this.track.style.transform = 'translate3d(' + offset + 'px,0,0)';
      this.slides.forEach(function (slide, idx) {
        var active = idx >= self.current && idx < self.current + s.slidesToShow;
        slide.classList.toggle('slick-active', active);
      });
    }

    if (this.dots) {
      var page = s.fade
        ? this.current
        : Math.round(this.current / Math.max(1, s.slidesToScroll));
      Array.from(this.dots.children).forEach(function (li, idx) {
        li.classList.toggle('slick-active', idx === page);
      });
    }

    if (this.prev) this.prev.classList.toggle('slick-disabled', this.current === 0 && !s.infinite);
    if (this.next) this.next.classList.toggle('slick-disabled', this.current >= max && !s.infinite);
    if (this.prev) this.prev.toggleAttribute('disabled', this.current === 0 && !s.infinite);
    if (this.next) this.next.toggleAttribute('disabled', this.current >= max && !s.infinite);

    if (!skipNavSync && this.linkedNav && this.linkedNav.current !== this.current) {
      this.linkedNav.goTo(this.current, true);
    }

    this.emit('change', { currentSlide: this.current });
  };

  NonoCarousel.prototype.goTo = function (idx, skipNavSync) {
    this.current = idx;
    this._update(skipNavSync);
  };

  NonoCarousel.prototype.nextSlide = function (loop) {
    var s = this.activeSettings;
    var step = s.slidesToScroll;
    var max = s.fade ? this.slides.length - 1 : Math.max(0, this.slides.length - s.slidesToShow);
    var next = this.current + step;
    if (next > max) next = (s.infinite || loop) ? 0 : max;
    this.goTo(next);
  };

  NonoCarousel.prototype.prevSlide = function () {
    var s = this.activeSettings;
    var step = s.slidesToScroll;
    var max = s.fade ? this.slides.length - 1 : Math.max(0, this.slides.length - s.slidesToShow);
    var prev = this.current - step;
    if (prev < 0) prev = s.infinite ? max : 0;
    this.goTo(prev);
  };

  NonoCarousel.prototype.on = function (evt, fn) {
    (this.handlers[evt] = this.handlers[evt] || []).push(fn);
    return this;
  };

  NonoCarousel.prototype.emit = function (evt, data) {
    (this.handlers[evt] || []).forEach(function (fn) { try { fn(data); } catch (e) {} });
  };

  NonoCarousel.prototype.destroy = function () {
    clearTimeout(this._timer);
    window.removeEventListener('resize', this._onResize);
    this.root.classList.remove('slick-initialized', 'slick-slider', 'slick-dotted', 'nono-carousel--fade', 'nono-carousel--slide');
    if (this.dots && this.dots.parentNode) this.dots.parentNode.removeChild(this.dots);
    registry.delete(this.root);
  };

  /* Lazy initialize: only when the carousel is near the viewport. */
  NonoCarousel.init = function (root, opts) {
    if (typeof root === 'string') root = document.querySelector(root);
    if (!root) return null;
    if (registry.has(root)) return registry.get(root);

    if (!('IntersectionObserver' in window)) {
      return new NonoCarousel(root, opts);
    }

    var pending = { root: root, opts: opts };
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          io.disconnect();
          new NonoCarousel(pending.root, pending.opts);
        }
      });
    }, { rootMargin: '200px' });
    io.observe(root);
    return null;
  };

  NonoCarousel.get = function (root) {
    if (typeof root === 'string') root = document.querySelector(root);
    return root ? registry.get(root) || null : null;
  };

  global.NonoCarousel = NonoCarousel;
})(window);
